import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/api/client";

const GateContext = createContext(null);

/**
 * App-wide passcode gate.
 *
 * States:
 *  - `loading` (initial check)
 *  - `unlocked` (verified with backend)
 *  - `locked` (no valid token — shows GateScreen)
 *
 * We DO NOT block the AuthCallback route (`#session_id=…`) so Emergent Google
 * Auth can complete without prompting for the passcode. The passcode is
 * already stored server-side (bcrypt) and unlock/verify use httpOnly cookies.
 */
export const GateProvider = ({ children }) => {
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);

  const check = useCallback(async () => {
    try {
      await api.get("/gate/verify");
      setState("unlocked");
    } catch {
      setState("locked");
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      // OAuth callback — let AuthCallback finish first. Re-check after.
      setState("unlocked");
      return;
    }
    check();
  }, [check]);

  const unlock = async (password) => {
    setError(null);
    try {
      await api.post("/gate/unlock", { password });
      setState("unlocked");
      return true;
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        (e?.response?.status === 429
          ? "Demasiados intentos. Espera unos minutos."
          : "Contraseña incorrecta");
      setError(msg);
      return false;
    }
  };

  const lock = async () => {
    try {
      await api.post("/gate/logout");
    } catch {
      /* ignore */
    }
    setState("locked");
  };

  return (
    <GateContext.Provider value={{ state, error, unlock, lock, check }}>
      {children}
    </GateContext.Provider>
  );
};

export const useGate = () => useContext(GateContext);
